import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { format } from "date-fns";
import { CalendarDays, Moon, Sun } from "lucide-react";
import { IMAGES } from "@/constants/site";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.13 },
  }),
};

export const Hero = ({ chip }) => {
  const [now, setNow] = useState(() => new Date());
  const ref = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const hour = now.getHours();
  const isNight = hour >= 19 || hour < 7;

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="relative h-[280px] md:h-[520px] lg:h-[600px] overflow-hidden bg-fr-navy" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute -inset-y-[10%] inset-x-0">
        <img
          src={IMAGES.heroDay}
          alt="Golfo di Napoli al tramonto con Castel Sant'Elmo e il Vesuvio"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ${isNight ? "opacity-0" : "opacity-100"}`}
          data-testid="hero-image-day"
        />
        <img
          src={IMAGES.heroNight}
          alt="Golfo di Napoli di notte con le luci della città"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ${isNight ? "opacity-100" : "opacity-0"}`}
          data-testid="hero-image-night"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/25" />
      <div className="absolute inset-0 fr-grain" />

      <div
        className="absolute top-3 right-3 md:top-5 md:right-5 z-20 flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-white border border-white/10"
        data-testid="hero-daynight-badge"
      >
        {isNight ? <Moon size={12} /> : <Sun size={12} />}
        <span>{isNight ? "Notte" : "Giorno"}</span>
        <span className="font-normal opacity-80">{format(now, "HH:mm")}</span>
      </div>

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-white font-extrabold tracking-tight leading-[1.02] text-[42px] md:text-[64px]">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block" data-testid="hero-title-line1">
              Noleggia
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-1">
            <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block" data-testid="hero-title-line2">
              la libertà.
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7, ease: "easeOut" }}
          className="mt-3 md:mt-4 text-white/90 text-xs md:text-base max-w-md md:max-w-xl"
          data-testid="hero-subtitle"
        >
          Noleggio auto a 5 minuti dall'aeroporto di Napoli – consegna in aeroporto gratuita.
          <br className="hidden md:block" /> Consegna da 5 a 90 min, senza carta di credito.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-4 left-3 md:bottom-16 md:left-5 z-20 flex items-center gap-2 rounded-full bg-fr-navy/95 text-white pl-3 pr-4 py-2 text-[11px] md:text-xs font-semibold shadow-search"
        data-testid="hero-search-chip"
      >
        <CalendarDays size={14} className="text-white/80" />
        <span>
          {chip.days} giorni • {chip.locationName}
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
